import type { Well } from '../types';
import { parseCompactWellsStream } from './compactParser';
import { cacheService } from './cache';

interface CachedSearch {
  query: string;
  results: Well[];
  timestamp: number;
}

// Rich search history item for gradient chips
interface RichSearchHistoryItem {
  query: string;
  facilityBreakdown: Record<string, number>;
  totalCount: number;
  timestamp: number;
}

interface OfflineCache {
  searches: CachedSearch[];
  lastUpdate: number;
  version?: string;
}

const CACHE_KEY = 'ziawellnav-offline-cache';
const RICH_SEARCH_HISTORY_KEY = 'searchHistoryV2';
const MAX_RICH_SEARCH_HISTORY = 25;
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_CACHED_SEARCHES = 50;
const OFFLINE_CACHE_VERSION = '1.2.0';

// County to basin mapping for New Mexico
export const COUNTY_BASINS: Record<string, string> = {
  'Eddy': 'Delaware',
  'Lea': 'Delaware',
  'Chaves': 'Permian',
  'Roosevelt': 'Permian',
  'Curry': 'Permian',
  'De Baca': 'Permian',
  'Lincoln': 'Permian',
  'San Juan': 'San Juan',
  'Rio Arriba': 'San Juan',
  'McKinley': 'San Juan',
  'Sandoval': 'San Juan',
  'Colfax': 'Raton'
};

// Texas Permian Basin counties
export const TX_COUNTY_BASINS: Record<string, string> = {
  'Andrews': 'Permian',
  'Borden': 'Permian',
  'Crane': 'Permian',
  'Dawson': 'Permian',
  'Ector': 'Permian',
  'Gaines': 'Permian',
  'Glasscock': 'Permian',
  'Howard': 'Permian',
  'Loving': 'Permian',
  'Martin': 'Permian',
  'Midland': 'Permian',
  'Pecos': 'Delaware',
  'Reagan': 'Permian',
  'Reeves': 'Delaware',
  'Upton': 'Permian',
  'Ward': 'Delaware',
  'Winkler': 'Delaware',
};

// Combined basins (NM + TX)
export const ALL_COUNTY_BASINS: Record<string, { basin: string; state: string }> = {
  // NM Counties
  ...Object.fromEntries(Object.entries(COUNTY_BASINS).map(([c, b]) => [c, { basin: b, state: 'NM' }])),
  // TX Counties
  ...Object.fromEntries(Object.entries(TX_COUNTY_BASINS).map(([c, b]) => [c, { basin: b, state: 'TX' }])),
};

// Get counties for a basin (optionally filtered by state)
export function getCountiesForBasin(basin: string, state?: string): string[] {
  if (state) {
    return Object.entries(ALL_COUNTY_BASINS)
      .filter(([_, info]) => info.basin === basin && info.state === state)
      .map(([county]) => county);
  }
  return Object.entries(ALL_COUNTY_BASINS)
    .filter(([_, info]) => info.basin === basin)
    .map(([county]) => county);
}

// Get unique basins
export function getBasins(): string[] {
  return [...new Set(Object.values(ALL_COUNTY_BASINS).map(info => info.basin))];
}

// Get all TX Permian counties
export function getTXPermianCounties(): string[] {
  return Object.keys(TX_COUNTY_BASINS);
}

class OfflineService {
  private cache: OfflineCache;
  private isOnline: boolean = navigator.onLine;

  constructor() {
    this.cache = this.loadCache();
    this.setupConnectionListeners();
    this.initCacheService();
  }

  private async initCacheService() {
    try {
      await cacheService.initialize();
      console.log('✅ Cache service initialized');
    } catch (error) {
      console.error('❌ Failed to init cache service:', error);
    }
  }

  private setupConnectionListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('🌐 Connection restored');
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('📡 Connection lost - using offline mode');
    });
  }

  private loadCache(): OfflineCache {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (stored) {
        const cache = JSON.parse(stored) as OfflineCache;
        if (cache.version === OFFLINE_CACHE_VERSION && Date.now() - cache.lastUpdate < CACHE_EXPIRY) {
          console.log('📦 Loaded search cache:', cache.searches.length, 'searches');
          return cache;
        }
      }
    } catch (error) {
      console.error('Failed to load cache:', error);
    }

    return {
      searches: [],
      lastUpdate: Date.now(),
      version: OFFLINE_CACHE_VERSION
    };
  }

  private saveCache() {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      console.error('Failed to save cache:', error);
    }
  }

  // ==================== Search Caching (localStorage - small) ====================

  /**
   * Cache search results (limited to 20 per query)
   */
  cacheSearch(query: string, results: Well[]) {
    this.cache.searches = this.cache.searches.filter(s => s.query.toLowerCase() !== query.toLowerCase());
    
    this.cache.searches.unshift({
      query,
      results: results.slice(0, 20),
      timestamp: Date.now()
    });

    if (this.cache.searches.length > MAX_CACHED_SEARCHES) {
      this.cache.searches = this.cache.searches.slice(0, MAX_CACHED_SEARCHES);
    }

    this.cache.lastUpdate = Date.now();
    this.saveCache();
  }

  /**
   * Get recent search queries
   */
  getRecentSearches(limit: number = 10): string[] {
    return this.cache.searches.slice(0, limit).map(s => s.query);
  }

  /**
   * Remove a recent search entry by query
   */
  removeRecentSearch(query: string) {
    this.cache.searches = this.cache.searches.filter(
      s => s.query.toLowerCase() !== query.toLowerCase()
    );
    this.cache.lastUpdate = Date.now();
    this.saveCache();
  }

  /**
   * Get cached search results
   */
  getCachedSearch(query: string): Well[] | null {
    const cached = this.cache.searches.find(s => s.query.toLowerCase() === query.toLowerCase());
    if (cached) {
      console.log('📦 Retrieved cached results for:', query);
      return cached.results;
    }
    return null;
  }

  // ==================== Rich Search History (Gradient Chips) ====================

  /**
   * Save rich search history item with facility breakdown for gradient chips
   */
  saveRichSearchHistory(item: RichSearchHistoryItem): void {
    try {
      const stored = localStorage.getItem(RICH_SEARCH_HISTORY_KEY);
      let history: RichSearchHistoryItem[] = stored ? JSON.parse(stored) : [];
      
      // Remove existing entry with same query (case-insensitive)
      history = history.filter(h => h.query.toLowerCase() !== item.query.toLowerCase());
      
      // Add new item at the beginning (LIFO)
      history.unshift(item);
      
      // Keep max items
      if (history.length > MAX_RICH_SEARCH_HISTORY) {
        history = history.slice(0, MAX_RICH_SEARCH_HISTORY);
      }
      
      localStorage.setItem(RICH_SEARCH_HISTORY_KEY, JSON.stringify(history));
      console.log('💾 Saved rich search history:', item.query, '| Total:', history.length);
    } catch (error) {
      console.error('Failed to save rich search history:', error);
    }
  }

  /**
   * Get rich search history for gradient chips
   */
  getRichSearchHistory(): RichSearchHistoryItem[] {
    try {
      const stored = localStorage.getItem(RICH_SEARCH_HISTORY_KEY);
      if (stored) {
        const history = JSON.parse(stored) as RichSearchHistoryItem[];
        console.log('📦 Loaded rich search history:', history.length, 'items');
        return history;
      }
    } catch (error) {
      console.error('Failed to load rich search history:', error);
    }
    return [];
  }

  /**
   * Remove a specific item from rich search history
   */
  removeRichSearchHistory(query: string): void {
    try {
      const stored = localStorage.getItem(RICH_SEARCH_HISTORY_KEY);
      if (stored) {
        let history: RichSearchHistoryItem[] = JSON.parse(stored);
        history = history.filter(h => h.query.toLowerCase() !== query.toLowerCase());
        localStorage.setItem(RICH_SEARCH_HISTORY_KEY, JSON.stringify(history));
        console.log('🗑️ Removed from rich search history:', query);
      }
    } catch (error) {
      console.error('Failed to remove from rich search history:', error);
    }
  }

  /**
   * Clear all rich search history
   */
  clearRichSearchHistory(): void {
    try {
      localStorage.removeItem(RICH_SEARCH_HISTORY_KEY);
      console.log('🗑️ Cleared all rich search history');
    } catch (error) {
      console.error('Failed to clear rich search history:', error);
    }
  }

  // ==================== County-Based Caching (via CacheService) ====================

  /**
   * Download and cache all wells for a specific county
   * Uses the existing cacheService which handles compression and IndexedDB storage
   */
  async downloadCounty(county: string, onProgress?: (percent: number) => void): Promise<number> {
    if (!this.isOnline) {
      throw new Error('Cannot download while offline');
    }

    console.log(`⬇️ Downloading wells for ${county}...`);
    onProgress?.(0);
    
    try {
      // Fetch wells with county filter using compact format
      const url = `/api/facilities/search?type=wells&format=compact&county=${encodeURIComponent(county)}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${county} wells: ${response.status}`);
      }

      onProgress?.(50);
      const wells = await parseCompactWellsStream(response);
      
      onProgress?.(75);
      // Use cacheService to store the wells (it handles compression and IndexedDB)
      await cacheService.cacheFacilityType('wells', county, wells);
      
      onProgress?.(100);
      console.log(`✅ Downloaded ${wells.length} wells for ${county}`);
      return wells.length;
    } catch (error) {
      console.error(`❌ Failed to download ${county}:`, error);
      throw error;
    }
  }

  /**
   * Download all counties in a basin
   */
  async downloadBasin(basin: string, onProgress?: (county: string, done: number, total: number) => void): Promise<number> {
    const counties = getCountiesForBasin(basin);
    
    if (counties.length === 0) {
      throw new Error(`Unknown basin: ${basin}`);
    }

    let totalWells = 0;
    for (let i = 0; i < counties.length; i++) {
      const county = counties[i];
      try {
        const count = await this.downloadCounty(county);
        totalWells += count;
        onProgress?.(county, i + 1, counties.length);
      } catch (error) {
        console.error(`Failed to download ${county}:`, error);
      }
    }

    return totalWells;
  }

  /**
   * Check if a specific county has cached data
   */
  async isCountyDownloaded(county: string): Promise<boolean> {
    try {
      const cached = await cacheService.getCachedData(county, 'wells');
      return cached !== null && cached.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get all wells for a county from cache
   */
  async getCountyWells(county: string): Promise<Well[]> {
    try {
      const cached = await cacheService.getCachedData(county, 'wells');
      return cached || [];
    } catch (error) {
      console.error(`Failed to get wells for ${county}:`, error);
      return [];
    }
  }

  /**
   * Get storage usage estimate
   */
  async getStorageEstimate(): Promise<{ used: number; quota: number; usedMB: string; quotaMB: string }> {
    try {
      if ('storage' in navigator && 'estimate' in (navigator as any).storage) {
        const estimate = await (navigator as any).storage.estimate();
        return {
          used: estimate.usage || 0,
          quota: estimate.quota || 0,
          usedMB: ((estimate.usage || 0) / (1024 * 1024)).toFixed(2) + ' MB',
          quotaMB: ((estimate.quota || 0) / (1024 * 1024)).toFixed(0) + ' MB'
        };
      }
    } catch (error) {
      console.error('Storage estimate failed:', error);
    }
    return { used: 0, quota: 0, usedMB: '0 MB', quotaMB: '0 MB' };
  }

  // ==================== General Methods ====================

  /**
   * Get online status
   */
  getOnlineStatus(): boolean {
    return this.isOnline;
  }

  /**
   * Check if we have any cached data (for verification indicator)
   */
  async hasValidCache(): Promise<boolean> {
    try {
      const cacheSize = await cacheService.getTotalCacheSize();
      return cacheSize.total > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    const storage = await this.getStorageEstimate();
    const syncInfo = await cacheService.getLastSyncInfo();
    const cacheSize = await cacheService.getTotalCacheSize();

    return {
      isOnline: this.isOnline,
      cachedSearches: this.cache.searches.length,
      totalCacheSize: cacheSize.total,
      storage,
      lastUpdate: this.cache.lastUpdate,
      lastSync: syncInfo?.timestamp || null,
      cacheAge: Date.now() - this.cache.lastUpdate
    };
  }

  /**
   * Clear all cached data (localStorage + IndexedDB via cacheService)
   */
  async clearCache() {
    // Clear localStorage searches
    this.cache = {
      searches: [],
      lastUpdate: Date.now(),
      version: OFFLINE_CACHE_VERSION
    };
    this.saveCache();
    
    // Clear cacheService IndexedDB
    await cacheService.clearCache();
    
    console.log('🗑️ Cleared all offline cache');
  }

  // ==================== Legacy Compatibility Methods ====================

  /**
   * @deprecated Use downloadCounty() instead
   */
  async preloadTopWells(): Promise<boolean> {
    console.log('ℹ️ preloadTopWells() is deprecated. Use downloadCounty() for county-based caching.');
    return true;
  }

  /**
   * @deprecated Use getCountyWells() for async access
   */
  searchCachedWells(_query: string): Well[] {
    // Sync method can't use IndexedDB, return empty
    return [];
  }

  /**
   * @deprecated Use getCountyWells() for async access
   */
  getAllCachedWells(): Well[] {
    // Sync method can't use IndexedDB, return empty
    return [];
  }

  /**
   * @deprecated Use cacheSearch for search results only
   */
  async cacheTopWells(_wells: Well[]): Promise<void> {
    console.log('ℹ️ cacheTopWells() is deprecated. Use downloadCounty() for county-based caching.');
  }
}

// Export singleton instance
export const offlineService = new OfflineService();
